/**
 * Invoice Analysis Modal
 * Upload a commercial invoice → AI extracts line items → shows compliance status
 * No declaration records are created — compliance identification only.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Typography, CircularProgress, Alert, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Divider,
  FormControl, InputLabel, Select, MenuItem, Accordion,
  AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AutoAwesome as AIIcon,
  CheckCircle as CheckIcon,
  Cancel as MissingIcon,
  ExpandMore as ExpandIcon,
  Receipt as InvoiceIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  analyzeInvoice,
  saveAnalysisRequirements,
  getSuppliers,
  InvoiceAnalysisResponse,
} from '../../../services/supplierDeclarationService';
import { brand } from '../../../theme';

interface InvoiceAnalysisModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const typeLabel: Record<string, string> = {
  origin: 'Origin Declaration',
  preferential: 'Preferential Origin',
  quality: 'Quality Certificate',
  safety: 'Safety Declaration',
  environmental: 'Environmental Declaration',
  other: 'Other',
};

const InvoiceAnalysisModal: React.FC<InvoiceAnalysisModalProps> = ({ open, onClose, onSaved }) => {
  const [file, setFile] = useState<File | null>(null);
  const [supplierId, setSupplierId] = useState('');
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<InvoiceAnalysisResponse | null>(null);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  React.useEffect(() => {
    if (open) {
      loadSuppliers();
      setFile(null);
      setResult(null);
      setSupplierId('');
    }
  }, [open]);

  const loadSuppliers = async () => {
    setLoadingSuppliers(true);
    try {
      const data = await getSuppliers();
      setSuppliers(data.map((s: any) => ({ id: s.id, name: s.name })));
    } catch {}
    finally { setLoadingSuppliers(false); }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleSaveRequirements = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const res = await saveAnalysisRequirements(result, supplierId || undefined);
      if (res.created === 0 && res.skipped > 0) {
        toast.info(`All ${res.skipped} requirement(s) already tracked — nothing new to save.`);
      } else {
        toast.success(
          `Saved ${res.created} pending requirement(s)${res.skipped > 0 ? ` (${res.skipped} already existed)` : ''}.`
        );
        onSaved?.();
        onClose();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to save requirements');
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyze = async () => {
    if (!file) { toast.error('Please select an invoice file'); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await analyzeInvoice(file, supplierId || undefined);
      setResult(res);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InvoiceIcon sx={{ color: brand.orange }} />
          <span>Analyze Invoice — Compliance Check</span>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Upload a <strong>commercial invoice</strong>. AI will extract all products and tell you
          which supplier declarations are required for each one. No records will be created.
        </Alert>

        {/* Upload controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 3, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            sx={{ borderColor: brand.orange, color: brand.orange }}
          >
            {file ? file.name : 'Select Invoice'}
            <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.tiff"
              onChange={handleFileChange} />
          </Button>

          <FormControl size="small" sx={{ width: 240 }}>
            <InputLabel>Supplier (optional)</InputLabel>
            <Select value={supplierId} label="Supplier (optional)"
              onChange={e => setSupplierId(e.target.value)}
              disabled={loadingSuppliers}>
              <MenuItem value=""><em>— Select to check satisfaction —</em></MenuItem>
              {suppliers.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
            onClick={handleAnalyze}
            disabled={!file || loading}
            sx={{ bgcolor: brand.orange, '&:hover': { bgcolor: '#c45e00' } }}
          >
            {loading ? 'Analysing…' : 'Analyse Invoice'}
          </Button>
        </Box>

        {/* Results */}
        {result && (
          <Box>
            {/* Invoice header */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#fafafa' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {result.supplier_name && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Supplier</Typography>
                    <Typography fontWeight={700}>{result.supplier_name}</Typography>
                  </Box>
                )}
                {result.supplier_country && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Country</Typography>
                    <Typography fontWeight={700}>{result.supplier_country}</Typography>
                  </Box>
                )}
                {result.invoice_number && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Invoice No</Typography>
                    <Typography fontWeight={700}>{result.invoice_number}</Typography>
                  </Box>
                )}
                {result.invoice_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography fontWeight={700}>{result.invoice_date}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Summary chips */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {result.line_items.length} product(s) found
              </Typography>
              <Chip label={`${result.total_requirements} declarations required`}
                size="small" variant="outlined" />
              <Chip icon={<CheckIcon />} label={`${result.total_satisfied} satisfied`}
                size="small" color="success" />
              <Chip icon={<MissingIcon />}
                label={`${result.total_missing} missing`}
                size="small" color={result.total_missing > 0 ? 'error' : 'default'} />
            </Box>

            {result.total_missing > 0 && !supplierId && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Select a supplier above to check which declarations they already have.
              </Alert>
            )}

            {/* Per-product accordion */}
            {result.line_items.map((item, idx) => (
              <Accordion key={idx} defaultExpanded={item.missing > 0}
                sx={{ mb: 1, border: `1px solid`, borderColor: item.missing > 0 ? 'error.light' : 'success.light', borderRadius: '8px !important' }}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2, flexWrap: 'wrap' }}>
                    <Typography fontWeight={600} sx={{ flex: 1 }}>
                      {item.product_description || `Product ${idx + 1}`}
                    </Typography>
                    {item.hs_code && <Chip label={`HS: ${item.hs_code}`} size="small" variant="outlined" />}
                    {item.origin_country && <Chip label={item.origin_country} size="small" variant="outlined" />}
                    {item.quantity && <Typography variant="body2" color="text.secondary">Qty: {item.quantity}</Typography>}
                    <Chip icon={<CheckIcon />} label={item.satisfied} size="small" color="success" />
                    {item.missing > 0 && <Chip icon={<MissingIcon />} label={`${item.missing} missing`} size="small" color="error" />}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {item.requirements.length === 0 ? (
                    <Alert severity="success" sx={{ py: 0.5 }}>
                      No declaration requirements found for this product.
                    </Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ width: 36 }}></TableCell>
                            <TableCell><strong>Required Declaration</strong></TableCell>
                            <TableCell><strong>Mandatory</strong></TableCell>
                            <TableCell><strong>Legal Basis</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {item.requirements.map((req, ri) => (
                            <TableRow key={ri}
                              sx={{ bgcolor: req.satisfied ? '#f0fdf4' : req.is_mandatory ? '#fff1f1' : '#fffbeb' }}>
                              <TableCell>
                                {req.satisfied
                                  ? <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                  : <MissingIcon sx={{ color: req.is_mandatory ? 'error.main' : 'warning.main', fontSize: 18 }} />}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {typeLabel[req.declaration_type] ?? req.declaration_type}
                                </Typography>
                                {!req.satisfied && (
                                  <Typography variant="caption" color={req.is_mandatory ? 'error.main' : 'warning.main'}>
                                    {req.is_mandatory ? 'Must request from supplier' : 'Optional'}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {req.is_mandatory
                                  ? <Chip label="Mandatory" size="small" color="error" />
                                  : <Chip label="Optional" size="small" />}
                              </TableCell>
                              <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                                {req.description || '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {result && result.total_missing > 0 && (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <MissingIcon />}
            onClick={handleSaveRequirements}
            disabled={saving}
            sx={{ bgcolor: brand.orange, '&:hover': { bgcolor: '#c45e00' } }}
          >
            {saving ? 'Saving…' : `Save ${result.total_missing} Missing Requirement(s)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceAnalysisModal;
